import { useEffect, useState } from 'react';
import axios from 'axios';
import { Coins } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  apply_used_as_is: 'Used as-is',
  apply_used_with_modification: 'Used with modification',
  apply_hit_issue: 'Tried & hit issue',
  apply_not_applicable: 'Not applicable',
  asset_reused: 'Asset you authored was applied',
};

export default function ContributionTokensBadge({ headers }) {
  const [tokens, setTokens] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/cop/tokens/mine`, { headers });
        if (mounted) setTokens(r.data);
      } catch { /* silent */ }
    };
    load();
    const onWin = () => load();
    window.addEventListener('focus', onWin);
    const t = setInterval(load, 60000);
    return () => {
      mounted = false;
      window.removeEventListener('focus', onWin);
      clearInterval(t);
    };
  }, [headers]);

  if (!tokens) return null;
  const balance = tokens.balance || 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-[#F59E0B] bg-[#F59E0B]/10 hover:bg-[#F59E0B]/15 border border-[#F59E0B]/20 transition-colors"
          data-testid="tokens-badge"
        >
          <Coins className="w-3 h-3" />
          {balance} contribution token{balance === 1 ? '' : 's'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" data-testid="tokens-popover">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">
              Balance
            </p>
            <span className="text-lg font-bold text-[#F59E0B]">{balance}</span>
          </div>
          <p className="text-[10px] text-[#4B5563]">
            Lifetime earned: <strong>{tokens.lifetime_earned}</strong>
          </p>
          <div className="border-t border-[#F3F4F6] pt-2">
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">
              Recent
            </p>
            {(tokens.recent_events || []).length === 0 ? (
              <p className="text-[11px] text-[#9CA3AF] italic">
                Apply a diffused asset via "Try it" to start earning.
              </p>
            ) : (
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {tokens.recent_events.slice(0, 10).map((e, i) => (
                  <li key={i} className="flex items-center justify-between text-[10px]" data-testid={`token-event-${i}`}>
                    <span className="text-[#4B5563]">
                      {SOURCE_LABELS[e.source] || e.source}
                      <span className="text-[#9CA3AF] ml-1">· {format(new Date(e.at), 'MMM d, HH:mm')}</span>
                    </span>
                    <span className="font-bold text-[#34C759]">+{e.amount}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
